import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';
import { todayKey } from './date';
import { indexCheckIns, type CheckInIndex } from './streak';
import {
  TINT_COLORS,
  type Category,
  type CategoryMeta,
  type CheckIn,
  type Goal,
  type NewGoalInput,
  type Tint,
  type UpdateGoalInput,
} from './types';

interface StoreState {
  goals: Goal[];
  checkIns: CheckIn[];
  categories: Category[];
  categoryMap: Map<string, CategoryMeta>;
  loading: boolean;
  error: string | null;
  today: string;
  checkInIndex: CheckInIndex;
  createGoal: (input: NewGoalInput) => Promise<Goal>;
  updateGoal: (id: string, patch: UpdateGoalInput) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  toggleToday: (goal: Goal) => Promise<void>;
  createCategory: (input: { label: string; tint: Tint; icon?: string | null }) => Promise<Category>;
  deleteCategory: (id: string, reassignTo?: string) => Promise<{ reassigned: number }>;
  reload: () => Promise<void>;
}

const Ctx = createContext<StoreState | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [today, setToday] = useState(() => todayKey());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, c, cs] = await Promise.all([
        api.listGoals(),
        api.listCheckIns(),
        api.listCategories(),
      ]);
      setGoals(g);
      setCheckIns(c);
      setCats(cs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Midnight rollover — bump `today` so UI re-derives "done today".
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const k = todayKey(now);
      if (k !== today) setToday(k);
    };
    const id = window.setInterval(tick, 60_000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [today]);

  const checkInIndex = useMemo(() => indexCheckIns(checkIns), [checkIns]);

  const categoryMap = useMemo<Map<string, CategoryMeta>>(() => {
    const m = new Map<string, CategoryMeta>();
    for (const c of cats) {
      m.set(c.key, {
        key: c.key,
        label: c.label.toUpperCase(),
        tint: c.tint,
        color: TINT_COLORS[c.tint],
        icon: c.icon,
      });
    }
    return m;
  }, [cats]);

  const createGoal = useCallback(async (input: NewGoalInput) => {
    const goal = await api.createGoal(input);
    setGoals(prev => [goal, ...prev]);
    return goal;
  }, []);

  const updateGoal = useCallback(async (id: string, patch: UpdateGoalInput) => {
    const goal = await api.updateGoal(id, patch);
    setGoals(prev => prev.map(g => (g.id === id ? goal : g)));
  }, []);

  const deleteGoal = useCallback(async (id: string) => {
    await api.deleteGoal(id);
    setGoals(prev => prev.filter(g => g.id !== id));
    setCheckIns(prev => prev.filter(c => c.goal_id !== id));
  }, []);

  const toggleToday = useCallback(
    async (goal: Goal) => {
      const day = todayKey();
      if (goal.type === 'todo') {
        const next = !goal.completed_at;
        await updateGoal(goal.id, { completed: next });
        return;
      }
      const done = checkInIndex.get(goal.id)?.has(day) ?? false;
      if (done) {
        await api.removeCheckIn(goal.id, day);
        setCheckIns(prev => prev.filter(c => !(c.goal_id === goal.id && c.day === day)));
      } else {
        const c = await api.addCheckIn(goal.id, day);
        setCheckIns(prev => [c, ...prev]);
      }
    },
    [checkInIndex, updateGoal],
  );

  const createCategory = useCallback(async (input: { label: string; tint: Tint; icon?: string | null }) => {
    const cat = await api.createCategory(input);
    setCats(prev => [...prev, cat]);
    return cat;
  }, []);

  const deleteCategory = useCallback(async (id: string, reassignTo?: string) => {
    const res = await api.deleteCategory(id, reassignTo);
    setCats(prev => prev.filter(c => c.id !== id));
    if (res.reassigned > 0 && reassignTo) {
      setGoals(prev =>
        prev.map(g => {
          const removed = cats.find(c => c.id === id);
          if (removed && g.cat === removed.key) return { ...g, cat: reassignTo };
          return g;
        }),
      );
    }
    return { reassigned: res.reassigned };
  }, [cats]);

  const value: StoreState = {
    goals,
    checkIns,
    categories: cats,
    categoryMap,
    loading,
    error,
    today,
    checkInIndex,
    createGoal,
    updateGoal,
    deleteGoal,
    toggleToday,
    createCategory,
    deleteCategory,
    reload,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): StoreState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStore must be used inside StoreProvider');
  return v;
}
